import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { DownloadedTasksService } from './downloaded-tasks.service';

export type TaskId = string;
type TaskTransientStatus = 'DOWNLOADING' | 'DOWNLOADFAILED' | 'SYNCING' | 'SYNCFAILED';
export type TaskStatus = 'DOWNLOADED' | TaskTransientStatus;
export type TaskStatusByTaskId = { [taskId: TaskId]: TaskStatus };
@Injectable({
  providedIn: 'root',
})
export class TasksStatusService implements OnDestroy {
  #downloadedTasksService = inject(DownloadedTasksService);

  #tasksStatus$: BehaviorSubject<TaskStatusByTaskId> = new BehaviorSubject({});
  #destroy$: Subject<void> = new Subject<void>();

  public constructor() {
    this.#downloadedTasksService.downloadedTasks$.pipe(takeUntil(this.#destroy$)).subscribe(downloadedTasks => {
      const currentTasksStatus = this.#tasksStatus$.getValue();
      const updatedTasksStatus = { ...currentTasksStatus };

      // remove transient sync status of task not downloaded
      const tasksStatusToRemove = Object.entries(updatedTasksStatus)
        .filter(([taskId, status]) => {
          const isTaskDownloaded = !!downloadedTasks.find(task => task.id === taskId);
          return !isTaskDownloaded && (status === 'SYNCING' || status === 'SYNCFAILED' || status === 'DOWNLOADED');
        })
        .map(([taskId]) => taskId);
      tasksStatusToRemove.forEach(taskId => {
        delete updatedTasksStatus[taskId];
      });
      downloadedTasks.forEach(downloadedTask => {
        const currentStatus = updatedTasksStatus[downloadedTask.id];
        if (!currentStatus || currentStatus === 'DOWNLOADFAILED' || currentStatus === 'DOWNLOADING') {
          updatedTasksStatus[downloadedTask.id] = 'DOWNLOADED';
        }
      });
      this.#tasksStatus$.next(updatedTasksStatus);
    });
  }

  public get statusByTaskId$(): Observable<TaskStatusByTaskId> {
    return this.#tasksStatus$.asObservable();
  }

  public ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  public setTaskAreDownloading(tasksId: TaskId[]) {
    this.setTasksStatus(tasksId, 'DOWNLOADING');
  }

  public setTasksDownloadHaveFailed(tasksId: TaskId[]) {
    this.setTasksStatus(tasksId, 'DOWNLOADFAILED');
  }

  public setTaskAreDownloaded() {
    this.#downloadedTasksService.refresh();
  }

  public setTasksAreSyncing(tasksId: TaskId[]) {
    this.setTasksStatus(tasksId, 'SYNCING');
  }

  public setTasksSyncHaveFailed(tasksId: TaskId[]) {
    this.setTasksStatus(tasksId, 'SYNCFAILED');
  }

  public setNotSignedTasksSyncIsDone(tasksId: TaskId[]) {
    this.setTasksStatus(tasksId, 'DOWNLOADED');
  }

  private setTasksStatus(tasksId: TaskId[], status: TaskStatus) {
    const currentTasksStatus = this.#tasksStatus$.getValue();
    const updatedTasksStatus = { ...currentTasksStatus };
    tasksId.forEach(taskId => {
      updatedTasksStatus[taskId] = status;
    });
    this.#tasksStatus$.next(updatedTasksStatus);
  }
}
